/**
 * BIGWA Core - Rate Limiter
 * 
 * Implementação de rate limiting usando Token Bucket algorithm.
 * Respeita limites de APIs externas como Google, OpenAlex, etc.
 */

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Configuração do rate limiter
 */
export interface RateLimiterConfig {
  /**
   * Número máximo de tokens (burst capacity)
   */
  maxTokens: number;

  /**
   * Taxa de refill (tokens por segundo)
   */
  refillRate: number;

  /**
   * Nome do limiter (para logging)
   */
  name?: string;
}

/**
 * Status do rate limiter
 */
export interface RateLimiterStatus {
  availableTokens: number;
  maxTokens: number;
  refillRate: number;
  isThrottled: boolean;
  queueLength: number;
}

// ============================================================================
// RATE LIMITER
// ============================================================================

/**
 * Rate Limiter usando Token Bucket algorithm
 * 
 * @example
 * // 10 requests por segundo, burst de 20
 * const limiter = new RateLimiter({ maxTokens: 20, refillRate: 10 });
 * 
 * // Antes de cada request
 * await limiter.acquire();
 * await fetch(url);
 */
export class RateLimiter {
  private tokens: number;
  private readonly maxTokens: number;
  private readonly refillRate: number; // tokens per second
  private lastRefill: number;
  private readonly queue: Array<() => void> = [];
  private readonly name: string;
  private refillInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config: RateLimiterConfig) {
    this.maxTokens = config.maxTokens;
    this.refillRate = config.refillRate;
    this.tokens = config.maxTokens; // Começa cheio
    this.lastRefill = Date.now();
    this.name = config.name ?? 'RateLimiter';

    // Refill automático a cada 100ms para processar queue
    this.startRefillInterval();
  }

  /**
   * Adquire um token. Aguarda se não houver tokens disponíveis.
   * 
   * @returns Promise que resolve quando o token é adquirido
   */
  async acquire(): Promise<void> {
    this.refill();

    if (this.tokens >= 1) {
      this.tokens -= 1;
      return;
    }

    // Sem tokens disponíveis, aguardar na fila
    return new Promise<void>((resolve) => {
      this.queue.push(resolve);
    });
  }

  /**
   * Tenta adquirir um token sem aguardar.
   * 
   * @returns true se conseguiu adquirir, false se não há tokens
   */
  tryAcquire(): boolean {
    this.refill();

    if (this.tokens >= 1) {
      this.tokens -= 1;
      return true;
    }

    return false;
  }

  /**
   * Adquire múltiplos tokens de uma vez.
   * Útil para operações batch.
   */
  async acquireMany(count: number): Promise<void> {
    for (let i = 0; i < count; i++) {
      await this.acquire();
    }
  }

  /**
   * Retorna status atual do limiter
   */
  getStatus(): RateLimiterStatus {
    this.refill();
    
    return {
      availableTokens: Math.floor(this.tokens),
      maxTokens: this.maxTokens,
      refillRate: this.refillRate,
      isThrottled: this.tokens < 1,
      queueLength: this.queue.length,
    };
  }

  /**
   * Reseta o limiter para estado inicial (cheio)
   */
  reset(): void {
    this.tokens = this.maxTokens;
    this.lastRefill = Date.now();
    
    // Processa toda a fila
    while (this.queue.length > 0 && this.tokens >= 1) {
      this.tokens -= 1;
      const resolve = this.queue.shift();
      resolve?.();
    }
  }

  /**
   * Para o refill automático (cleanup)
   */
  dispose(): void {
    if (this.refillInterval) {
      clearInterval(this.refillInterval);
      this.refillInterval = null;
    }
  }

  /**
   * Tempo estimado de espera em ms (0 se há tokens disponíveis)
   */
  getEstimatedWaitTime(): number {
    this.refill();
    
    if (this.tokens >= 1) {
      return 0;
    }

    // Quantos tokens faltam para atender a fila + 1
    const tokensNeeded = this.queue.length + 1 - this.tokens;
    
    // Tempo para gerar esses tokens
    return Math.ceil((tokensNeeded / this.refillRate) * 1000);
  }

  // ============================================================================
  // MÉTODOS PRIVADOS
  // ============================================================================

  private refill(): void {
    const now = Date.now();
    const elapsedSeconds = (now - this.lastRefill) / 1000;
    
    // Calcula quantos tokens foram gerados
    const tokensToAdd = elapsedSeconds * this.refillRate;
    
    // Adiciona tokens sem exceder máximo
    this.tokens = Math.min(this.maxTokens, this.tokens + tokensToAdd);
    this.lastRefill = now;
  }

  private processQueue(): void {
    this.refill();
    
    // Processa a fila enquanto há tokens
    while (this.queue.length > 0 && this.tokens >= 1) {
      this.tokens -= 1;
      const resolve = this.queue.shift();
      resolve?.();
    }
  }

  private startRefillInterval(): void {
    // Processa fila a cada 100ms
    this.refillInterval = setInterval(() => {
      this.processQueue();
    }, 100);
  }
}

// ============================================================================
// RATE LIMITERS PRÉ-CONFIGURADOS
// ============================================================================

/**
 * Configurações de rate limit para APIs conhecidas
 */
export const RATE_LIMIT_PRESETS = {
  /**
   * Google APIs - 10 requests/segundo (conservador)
   */
  GOOGLE: {
    maxTokens: 20,
    refillRate: 10,
    name: 'Google API',
  },

  /**
   * OpenAlex - 100k/dia ≈ 1.15/segundo, mas permite burst
   */
  OPENALEX: {
    maxTokens: 50,
    refillRate: 1,
    name: 'OpenAlex API',
  },

  /**
   * Semantic Scholar - 100 requests/5min = 0.33/segundo
   */
  SEMANTIC_SCHOLAR: {
    maxTokens: 10,
    refillRate: 0.33,
    name: 'Semantic Scholar API',
  },

  /**
   * CrossRef - Polite pool, ~50/segundo com mailto
   */
  CROSSREF: {
    maxTokens: 50,
    refillRate: 30,
    name: 'CrossRef API',
  },

  /**
   * Rate limit genérico conservador
   */
  DEFAULT: {
    maxTokens: 10,
    refillRate: 5,
    name: 'Default',
  },
} as const;

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Cria rate limiter para API específica
 */
export const createRateLimiter = (
  preset: keyof typeof RATE_LIMIT_PRESETS | RateLimiterConfig
): RateLimiter => {
  if (typeof preset === 'string') {
    return new RateLimiter(RATE_LIMIT_PRESETS[preset]);
  }
  return new RateLimiter(preset);
};

// ============================================================================
// DECORATOR / WRAPPER
// ============================================================================

/**
 * Wrapper para adicionar rate limiting a uma função
 * 
 * @example
 * const limitedFetch = withRateLimit(fetch, limiter);
 * await limitedFetch(url); // Automaticamente aguarda se throttled
 */
export const withRateLimit = <T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  limiter: RateLimiter
): T => {
  return (async (...args: Parameters<T>) => {
    await limiter.acquire();
    return fn(...args);
  }) as T;
};
